import {Hotkey, IconButton} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useState} from 'react';

import {ToggleMinimizeRunSelectorButton} from '../../ToggleMinimizeRunSelectorButton';
import {CrossProjectRunsDrawer} from '../CrossProjectRunsDrawer/CrossProjectRunsDrawer';
import {useRunSetGroupSelectionGrouping} from '../RunSelectorRunSetContext';
import {type DisabledFeaturesMap} from '../types';

type WBTableTitleActionsProps = {
  disabledFeatures?: DisabledFeaturesMap;
  isTableExpanded?: boolean;
  toggleMinimize?: (method: 'button' | 'hotkey') => void;
  toggleExpanded: () => void;
};

export const WBTableTitleActions = ({
  disabledFeatures,
  isTableExpanded,
  toggleMinimize,
  toggleExpanded,
}: WBTableTitleActionsProps) => {
  const [isCrossProjectRunsDrawerOpen, setIsCrossProjectRunsDrawerOpen] =
    useState(false);

  const grouping = useRunSetGroupSelectionGrouping();
  const isGrouped = grouping.length > 0;

  const isMinimizeAllowed = !isTableExpanded && toggleMinimize != null;
  const isCrossProjectPinsAllowed =
    !disabledFeatures?.['pin-cross-project-runs'] && !isGrouped;

  return (
    <TailwindContents>
      <div className="flex items-center gap-4">
        {isCrossProjectPinsAllowed && (
          <IconButton
            id="chameleon-cpr-trigger"
            icon="import-corner-arrow"
            aria-label="Select runs from another project"
            variant="ghost"
            onPress={() => setIsCrossProjectRunsDrawerOpen(true)}
            tooltip={{children: 'Select runs from another project'}}
          />
        )}
        {isMinimizeAllowed && (
          <ToggleMinimizeRunSelectorButton
            variant="ghost"
            onToggle={() => toggleMinimize?.('button')}
            toMinimize
          />
        )}
        <IconButton
          data-test="expand-toggle"
          aria-label={
            isTableExpanded
              ? 'Collapse runs to sidebar'
              : 'Expand full runs table'
          }
          variant="ghost"
          icon={isTableExpanded ? 'expand-left' : 'expand-right'}
          onPress={toggleExpanded}
          tooltip={{
            children: (
              <>
                {isTableExpanded ? 'Collapse to sidebar' : 'Expand full table'}
                <Hotkey
                  ml={4}
                  name={
                    isTableExpanded
                      ? 'Collapse to sidebar'
                      : 'Expand full table'
                  }
                  keys={['Primary', 'KeyJ']}
                />
              </>
            ),
          }}
        />
        {isCrossProjectRunsDrawerOpen && (
          <CrossProjectRunsDrawer
            isOpen={isCrossProjectRunsDrawerOpen}
            onOpenChange={setIsCrossProjectRunsDrawerOpen}
          />
        )}
      </div>
    </TailwindContents>
  );
};
